/**
 * Platform detection utilities for iOS-specific behavior
 */

/**
 * Detect if running on iOS (iPhone, iPad, iPod)
 * Handles both Safari and iOS Chrome (which uses WebKit)
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  // Modern detection
  const isIOSDevice = /iPhone|iPad|iPod/.test(navigator.userAgent);
  
  // iPad with iPadOS 13+ reports as Mac, check for touch
  const isIPadOS = navigator.userAgent.includes('Mac') && 
                   'ontouchend' in document;
  
  return isIOSDevice || isIPadOS;
}

/**
 * Detect if running in Safari specifically
 */
export function isSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent;
  return ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('CriOS');
}

/**
 * Check if DeviceOrientation requires permission (iOS 13+)
 */
export function requiresOrientationPermission(): boolean {
  return (
    typeof DeviceOrientationEvent !== 'undefined' &&
    typeof (DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === 'function'
  );
}

/**
 * Check if currently in standalone PWA mode
 */
export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator && (window.navigator as unknown as { standalone: boolean }).standalone === true)
  );
}

/**
 * Get iOS version or null if not iOS
 */
export function getIOSVersion(): number | null {
  if (!isIOS()) return null;
  
  const match = navigator.userAgent.match(/OS (\d+)_/);
  return match ? parseInt(match[1], 10) : null;
}
